import React, { useState } from "react";
import { Icon, Input, Alert } from "antd";
import qs from "qs";
import { compose, withProps } from "recompose";
import { connect } from "react-redux";
import { Form, FormItem } from "@worcket/common/components/controls";
import { withForm, isLoading } from "@worcket/common/hocs";
import { useTranslation } from "react-i18next";

import Centered from "../../components/display/Centered";
import { signup, SIGNUP } from "../../store/actions/session";
import logo from "../../assets/logo-black.svg";
import Button from "../../components/Button";
import {
  passwordValidateRules,
  passwordCompareRules
} from "../../utils/password";

import styled from "styled-components/macro";

const ContainerForm = styled(Form)`
  width: 400px;

  .title {
    text-align: center;
    margin-bottom: 30px;
    font-size: 22px;
  }

  .company-name {
    font-weight: bold;
  }

  .logo {
    margin-bottom: 40px;
    display: flex;
    justify-content: center;

    img {
      max-width: 160px;
    }
  }
`;

const BlockButton = styled(Button)`
  width: 100%;
`;

const PasswordInput = props => {
  const [visible, setVisible] = useState(false);
  return (
    <Input
      {...props}
      type={visible ? "text" : "password"}
      suffix={
        <Icon
          type={visible ? "eye" : "eye-invisible"}
          onClick={() => setVisible(!visible)}
          style={{ color: "rgba(0,0,0,.45)", cursor: "pointer" }}
        />
      }
    />
  );
};

const SignUp = ({ form, loading, onSubmit, companyName, name, error }) => {
  const { t } = useTranslation();
  return (
    <Centered>
      <ContainerForm onSubmit={onSubmit}>
        <div className="logo">
          <img src={logo} alt="Worcket" />
        </div>
        {error ? (
          <Alert
            data-test="invitation-error"
            message={t("auth:signup.errorLink")}
            type="error"
            showIcon
          />
        ) : (
          <div>
            <h1 className="title" data-test="sign-up-title">
              {companyName ? (
                <span>
                  {t("auth:signup.companyInvite")}{" "}
                  <span className="company-name">{companyName}</span>
                </span>
              ) : (
                <span>{t("auth:signup.worcketInvite")}</span>
              )}
            </h1>
            <fieldset disabled={loading}>
              <FormItem
                form={form}
                name="name"
                rules={[
                  {
                    required: true,
                    message: t("auth:signup.emptyName")
                  }
                ]}
                initialValue={name}
                render={
                  <Input
                    data-test="name-input"
                    prefix={
                      <Icon type="user" style={{ color: "rgba(0,0,0,.25)" }} />
                    }
                    placeholder={t("auth:signup.placeholderName")}
                  />
                }
              />
              <FormItem
                form={form}
                name="password"
                rules={passwordValidateRules}
                render={
                  <PasswordInput
                    data-test="password-input"
                    prefix={
                      <Icon type="lock" style={{ color: "rgba(0,0,0,.25)" }} />
                    }
                    placeholder={t("auth:signup.placeholderPassword")}
                  />
                }
              />
              <FormItem
                form={form}
                name="repeatedPassword"
                rules={passwordCompareRules(form, "password")}
                render={
                  <PasswordInput
                    data-test="repeat-password-input"
                    prefix={
                      <Icon type="lock" style={{ color: "rgba(0,0,0,.25)" }} />
                    }
                    placeholder={t("auth:signup.repeatPassword")}
                  />
                }
              />
              <BlockButton
                size="xl"
                type="primary"
                htmlType="submit"
                loading={loading}
                block={true}
                className="sign-up-btn"
              >
                {t("auth:signup.createAccount")}
              </BlockButton>
            </fieldset>
          </div>
        )}
      </ContainerForm>
    </Centered>
  );
};

const enhancer = compose(
  withProps(({ location }) => {
    const { name, companyName, code: invitationCode } = qs.parse(
      location.search,
      { ignoreQueryPrefix: true }
    );
    return {
      name,
      companyName,
      invitationCode,
      error: !invitationCode
    };
  }),
  connect(
    undefined,
    { signup }
  ),
  isLoading(SIGNUP),
  withForm({
    selector: (_, { name }) => ({ name }),
    onSubmit: (values, { signup, invitationCode }) => {
      signup({
        name: values.name,
        password: values.password,
        code: invitationCode
      });
    }
  })
);

export default enhancer(SignUp);
